import { Fragment, h } from 'preact'
import { useEffect, useMemo, useState, useCallback, useRef } from 'preact/hooks'
import {
  useChatChannel,
} from 'lib/chatHooks'
import Header from 'components/Header'
import Loading from 'components/Loading'
import ErrorMessage from 'components/ErrorMessage'
import ChatChannelAvatar from 'components/ChatChannelAvatar'
import { DMChatChannelHeader } from './DMChatChannelHeader'
import { ChatWindow } from './ChatWindow'
import { useMutedUsers, useMyOrganizations } from 'lib/membershipAppStateHooks'
import { useAppActions } from 'lib/appState'
import { useGlobalCall } from 'lib/GlobalCallProvider'
import { hubChatDisplayName } from './hubChatDisplayName'
import { renderChatChannelAvatarValue } from './renderChatChannelAvatarValue'
import './index.sass'


export function ChatChannel({
  organization,
  myPublicProfile,
  chatChannelUid,
  chatChannelSynopsis,
  messageUid,
  setForwarding,
  chatChip,
  openforwardWindow,
  setOpenforwardWindow,
  setSelectedChats,
  setChatChip,
  toggleChatSelection,
  ...props
}) {
  const {
    chatChannel,
    chatMessages,
    chatMessagesLoadingError,
    pendingChatMessages,
    createChatMessage,
    editChatMessage,
    replyToMessage,
    markAllChatMessagesAsRead,
    deleteChatMessage,
    undoDeleteChatMessage,
    starChatMessage,
    unstarChatMessage,
    starredChatMessages,
    deleteChatChannel,
    leaveChannel,
    createChatMessageReaction,
    removeChatMessageReaction,
    markChatMessageAsRead,
  } = useChatChannel(chatChannelUid, messageUid, 'OrganizationChatPage')

  const { listmuted, mutedUsers: originalMutedUsers } = useMutedUsers('OrganizationChatPage')
  const { myOrganizations } = useMyOrganizations('ChatChannel')
  const [isOtherMemberAdmin, setIsOtherMemberAdmin] = useState(false)

  const mutedUsers = useMemo(() => originalMutedUsers, [originalMutedUsers])

  useEffect(() => {
    if(organization?.did) {
      listmuted({ organizationDid: organization.did })
    }
  }, [listmuted, organization?.did])

  const { takeAction } = useAppActions(
    'OrganizationChatPage',
    chatChannelUid,
  )

  useEffect(() => {
    if (chatChannel) {
      takeAction('chat.loadStarredMessages', chatChannelUid)
    }
  }, [chatChannel])


  const globalCall = useGlobalCall()

  const handleInitiateVideoCall = useCallback(() => {
    if (!globalCall) return
    globalCall.initiateCall({
      chatChannelUid,
      callType: 'video',
      chatName: chatChannel.name,
      organizationApikey: organization.apikey,
      memberUserDids: chatChannel.memberUserDids,
    })
  }, [
    globalCall, chatChannelUid, chatChannel.name,
    organization.apikey, chatChannel.memberUserDids,
  ])

  const handleInitiateAudioCall = useCallback(() => {
    if (!globalCall) return
    globalCall.initiateCall({
      chatChannelUid,
      callType: 'audio',
      chatName: chatChannel.name,
      organizationApikey: organization.apikey,
      memberUserDids: chatChannel.memberUserDids,
    })
  }, [
    globalCall, chatChannelUid, chatChannel.name,
    organization.apikey, chatChannel.memberUserDids,
  ])

  const [activeCallInfo, setActiveCallInfo] = useState(null)
  const pollRef = useRef(null)

  const pollActiveCall = useCallback(async () => {
    if (!globalCall || !chatChannelUid) return
    const info = await globalCall.checkActiveCall(chatChannelUid)
    setActiveCallInfo(info)
  }, [globalCall, chatChannelUid])

  useEffect(() => {
    pollActiveCall()
    pollRef.current = setInterval(pollActiveCall, 5000)
    return () => clearInterval(pollRef.current)
  }, [pollActiveCall])

  const isInThisCall = !!(
    globalCall?.activeCall &&
    globalCall.activeCall.chatChannelUid === chatChannelUid
  )

  const hasActiveCallInChat = !!(activeCallInfo && activeCallInfo.hasActiveCall)
  const isKickedFromCall = !!(activeCallInfo && activeCallInfo.isKicked)

  const handleJoinActiveCall = useCallback(() => {
    if (!globalCall || !activeCallInfo?.roomName) return
    globalCall.joinActiveCall({
      roomName: activeCallInfo.roomName,
      callType: 'video',
      chatChannelUid,
      chatName: chatChannel?.name,
    })
  }, [globalCall, activeCallInfo, chatChannelUid, chatChannel?.name])


  const [forwardMessageUid, setForwardMessageUid] = useState('')
  const [forwardedMessageContent, setForwardedMessageContent] = useState('')
  if (chatMessagesLoadingError) return <ErrorMessage error={chatMessagesLoadingError} />
  if (!chatChannel) return <Loading type="block" />

  if (chatMessagesLoadingError) {
    return <ErrorMessage error={chatMessagesLoadingError} />
  }

  if (!chatChannel) {
    return <Loading type="block" />
  }

  let isUserMutedForDM = false
  let isUserMutedForHubchat = false

  if (mutedUsers && Array.isArray(mutedUsers)) {
    isUserMutedForDM = mutedUsers.some(muted =>
      muted.user_did === myPublicProfile.did &&
      muted.organization_did === organization.did &&
      (muted.mute_type === 'ALL' || muted.mute_type === 'DM')
    )
    isUserMutedForHubchat = mutedUsers.some(muted =>
      muted.user_did === myPublicProfile.did &&
      muted.organization_did === organization.did &&
      (muted.mute_type === 'ALL' || muted.mute_type === 'hubchat')

    )
  }

  const isHubChat = chatChannel.type === 'public_hub_chat' || chatChannel.type === 'private_hub_chat'
  const isGeneral = chatChannel.type === 'public_hub_chat'

  const dmMute = chatChannel.type === 'dm' && isUserMutedForDM
  const generalMute = chatChannel.type === 'public_hub_chat' && isUserMutedForHubchat

  const isOneToOne = chatChannel?.memberUserDids?.length === 2

  const unread = chatChannelSynopsis?.unread || 0

  const addReactionToMessage = async (messageUid, emoji) => {
    try {
      await createChatMessageReaction(messageUid, emoji)
    } catch (error) {
      console.error(`Failed to add reaction to message ${messageUid}`, error)
    }
  }

  const handleRemoveReaction = async (messageUid, emoji) => {
    try {
      await removeChatMessageReaction(messageUid, emoji)

    } catch (error) {
      console.error(`Failed to remove reaction from message ${messageUid}`, error)
    }
  }

  return (
    <ChatWindow
      {...{
        organization,
        myPublicProfile,
        memberUserDids: chatChannel.memberUserDids,
        chatImage: chatChannel.imageUrl,
        chatname:chatChannel.name,
        uid: chatChannel.uid,
        loading: false,
        disabled: false,
        chatChannelUid,
        messageUid,
        chatChannel,
        setForwarding,
        setForwardMessageUid,
        setForwardedMessageContent,
        forwardedMessageContent,
        chatChip,
        forwardMessageUid,
        openforwardWindow,
        setOpenforwardWindow,
        setSelectedChats,
        setChatChip,
        toggleChatSelection,
        header: isHubChat ? (
          <Fragment>
            <ChatChannelAvatar size="sm" value={renderChatChannelAvatarValue(chatChannel.imageUrl)} />
            <Header size="lg">{hubChatDisplayName({
              name: chatChannel.name,
              type: chatChannel.type,
              orgName: myOrganizations?.find(o => o.apikey === chatChannel.organizationApikey)?.name || chatChannel.organizationApikey,
            })}</Header>
          </Fragment>
        ) : <DMChatChannelHeader {...{
          organization,
          myPublicProfile,
          chatname:chatChannel.name,
          chatChannel,
          memberUserDids: chatChannel.memberUserDids,
          onAdminStatusChange: setIsOtherMemberAdmin,
          chatImage: chatChannel.imageUrl,
        }} />,
        chatMessages,
        pendingChatMessages,
        markAllChatMessagesAsRead,
        createChatMessage,
        editChatMessage,
        replyToMessage,
        starChatMessage,
        unstarChatMessage,
        createdAt: isHubChat ? null : chatChannel.createdAt,
        creatorUserDid: isHubChat ? null : chatChannel.creatorUserDid,
        unread,
        starredChatMessages,
        isOneToOne,
        deleteChatMessage,
        undoDeleteChatMessage,
        addReactionToMessage,
        handleRemoveReaction,
        deleteChatChannel,
        leaveChannel,
        dmMute,
        generalMute,
        isOtherMemberAdmin,
        markChatMessageAsRead,
        onInitiateVideoCall: handleInitiateVideoCall,
        onInitiateAudioCall: handleInitiateAudioCall,
        hasActiveCallInChat,
        isInThisCall,
        isKickedFromCall,
        onJoinActiveCall: handleJoinActiveCall,
        activeCallParticipantCount: activeCallInfo?.participantCount || 0,
        ...props,
      }}
    />
  )
}
